import * as amplitude from '@amplitude/analytics-browser';
import { track } from '@amplitude/analytics-browser';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { filter, interval, take, takeWhile, tap } from 'rxjs';
import { Placement, Product } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { EventsService } from '../../services/events.service';
import { OnboardingFlowService } from '../../services/onboarding-flow.service';
import { AudioTrack, AudioTrackComponent } from '../../shared/audio-track/audio-track.component';
import { CheckoutComponent } from '../../shared/checkout/checkout.component';
import { ModalComponent, ModalData } from '../../shared/modal/modal.component';
import { UserReview, UserReviewComponent } from '../../shared/user-review/user-review.component';

@Component({
  standalone: true,
  imports: [CommonModule, CheckoutComponent, MatExpansionModule, UserReviewComponent, AudioTrackComponent, MatButtonModule],
  selector: 'app-onboarding-paywall',
  templateUrl: './paywall.component.html',
  styleUrls: ['./paywall.component.scss']
})
export class PaywallComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('topCheckout') topCheckout: ElementRef;
  @ViewChildren('checkout') checkoutComponents: QueryList<CheckoutComponent>;
  @ViewChildren('audioTrackRef') audioTracks!: QueryList<AudioTrackComponent>;

  selectedProduct: Product;

  discountTitle: string = '60%';
  initialDiscountTitle: string = '';
  timer: string = '10:00';

  email: string | undefined;
  name: string | undefined;
  stripeCustomerId: string | undefined;

  purchaseButtonTitle = 'Continue';

  topTitle: string;

  tracks: AudioTrack[];

  placement: Placement = 'quiz';

  reviews: UserReview[] = [
    {
      name: 'Hannah, Detroit',
      avatarUrl: 'assets/avatars/1.png',
      message: 'There is a lot of variety... I get to think about trying things I wouldn’t do in real life and it’s really liberating',
      subtitle: '34 years',
      rate: 4.9,
    },
    {
      name: 'Eliza Geidt',
      avatarUrl: 'assets/avatars/2.png',
      message: `It's definitely quality time with yourself and gives you the ability to explore body and pleasure in a completely new way.`,
      subtitle: '41 years',
      rate: 5,
    },
    {
      name: 'Celia Levin',
      avatarUrl: 'assets/avatars/3.png',
      message: `It's an excellent blend of fantasy and details grounded in reality that gets me going`,
      subtitle: '24 years',
      rate: 4.8,
    },
  ];

  private isActive: boolean = true; //to kill the subscriptions

  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private flowService: OnboardingFlowService, 
    private pixel: EventsService) {}

  ngOnInit(): void {
    this.stripeCustomerId = this.flowService.getStripeCustomerId();
    this.name = this.flowService.getName();
    this.tracks = this.flowService.getPaywallAudioSamples();
    this.topTitle = this.flowService.getTopTitle();

    this.authService.user$.pipe(
      take(1),
      tap(user => this.email = user?.email || undefined),
    ).subscribe();    
  }

  ngAfterViewInit(): void {
    this.startTimer();

    this.pixel.track('checkout_page_opened');
    track('web_checkout_view');
  }

  ngOnDestroy(): void {
    this.isActive = false;
  }

  payButtonClicked(): void {
    this.checkoutComponents.first.purchaseButtonClicked();
  }

  topButtonClicked(): void {
    this.scrollToCheckout();
  }

  handleDiscountTitle(title: string) {
    this.discountTitle = title;

    if (!this.initialDiscountTitle) {
      this.initialDiscountTitle = title;
    }
  }

  handlePurchaseButtonTitle(title: string) {
    this.purchaseButtonTitle = title;
  }

  getInsufficientFundsAction(): (() => void) | undefined {
    if (this.placement === 'quiz_discount') {
      //nothing to offer already
      return;
    }

    return () => {
      //TODO: maybe show another animation in this case
      //TODO: maybe introduce placement with cheapest plans possible for this case
      this.switchToDiscount(`We see that there are insufficient funds, but at FemSecret we are trying to support as many people as possible. That's why we are offering you an additional discount`);
    };
  }

  onPlay(currentTrack: AudioTrackComponent): void {
    this.audioTracks.forEach(trackComponent => {
      if (trackComponent !== currentTrack) {
        trackComponent.pause();
      }
    });
  }

  onPurchaseCanceled() {
    if (this.placement === 'quiz_discount') {
      //nothing to offer more
      return;
    }

    this.switchToDiscount('We want you to succeed, which is why we are offering you an additional discount');
  }

  private scrollToCheckout() {
    this.topCheckout.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private switchToDiscount(message: string) {
    this.dialog.open<ModalComponent, ModalData>(ModalComponent, {
      data: {
        title: 'Special Discount Unlocked',
        message,
        okButtonTitle: 'Get My Discount',
        rive: {
          fileName: 'gift_box',
          animations: ['Timeline 1'],
          ratio: 1.1 / 1,
        },
      }
    })
      .afterClosed()
      .pipe(
        tap(() => {
          this.placement = 'quiz_discount';
          amplitude.track('web_discount_shown');
          this.scrollToCheckout();
        }),
      )
      .subscribe();
  }

  private startTimer(): void {
    const secs = 10 * 60;

    interval(1000)
      .pipe(
        filter(passedSecs => passedSecs > 0),
        tap((passedSecs) => {
          const leftSecs = 60 - (passedSecs % 60);
          const leftSecsLabel = leftSecs > 9 ? `${leftSecs}` : `0${leftSecs}`;
          const minsLeft = Math.floor((secs - passedSecs) / 60);

          this.timer = `${minsLeft}:${leftSecsLabel}`;
        }),
        takeWhile(() => this.isActive),
      )
      .subscribe();
  }
}
